"use client";

import { Form, Field as FormischField, type SubmitHandler, setInput, useForm } from "@formisch/react";
import { authClient } from "@repo/auth/client";
import { Button, buttonVariants } from "@repo/design-system-2/components/ui/button";
import { Checkbox } from "@repo/design-system-2/components/ui/checkbox";
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@repo/design-system-2/components/ui/dialog";
import { Field, FieldError, FieldGroup, FieldLabel } from "@repo/design-system-2/components/ui/field";
import { Input } from "@repo/design-system-2/components/ui/input";
import { Spinner } from "@repo/design-system-2/components/ui/spinner";
import { IconPlus } from "@tabler/icons-react";
import { useRouter } from "next/navigation";
import { useAction } from "next-safe-action/hooks";
import { useState } from "react";
import { EntitySearch } from "../[seriesId]/[feisId]/manage/_components/entity-search";
import { createEventSchema } from "../[seriesId]/[feisId]/manage/_components/validation";
import { createEvent } from "./action-create-event";

export function CreateEventDialog() {
    const router = useRouter();
    const { data: session, isPending } = authClient.useSession();
    const [open, setOpen] = useState(false);
    const [selectedRegion, setSelectedRegion] = useState<{
        id: string;
        label: string;
        shortCode?: string | null;
    } | null>(null);

    const form = useForm({
        initialInput: {
            isWorldQualifier: false,
            regionId: "",
            seriesName: "",
            seriesShortCode: "",
        },
        schema: createEventSchema,
    });

    const createAction = useAction(createEvent, {
        onSuccess({ data }) {
            if (data) {
                setOpen(false);
                router.push(`/feiseanna/${data.seriesLabelLexeme}/${data.eventShortCode}/manage`);
                router.refresh();
            }
        },
    });

    const handleSubmit: SubmitHandler<typeof createEventSchema> = (output) => {
        createAction.execute(output);
    };

    if (isPending || session?.user.role !== "admin") {
        return null;
    }

    return (
        <>
            <Button onClick={() => setOpen(true)} type="button" variant="outline">
                <IconPlus />
                Create Event
            </Button>

            <Dialog onOpenChange={setOpen} open={open}>
                <DialogContent className="sm:max-w-lg">
                    <DialogHeader>
                        <DialogTitle>Create Event</DialogTitle>
                        <DialogDescription>
                            Create a new feis series and its first event. You can add dates and other details on the
                            manage page.
                        </DialogDescription>
                    </DialogHeader>

                    <Form of={form} onSubmit={handleSubmit}>
                        <FieldGroup>
                            <FormischField of={form} path={["seriesName"]}>
                                {(field) => (
                                    <Field data-invalid={field.errors !== null}>
                                        <FieldLabel htmlFor="create-series-name">Series Name</FieldLabel>
                                        <Input
                                            {...field.props}
                                            disabled={createAction.isExecuting}
                                            id="create-series-name"
                                            value={field.input ?? ""}
                                        />
                                        {field.errors && (
                                            <FieldError errors={field.errors.map((message) => ({ message }))} />
                                        )}
                                    </Field>
                                )}
                            </FormischField>

                            <FormischField of={form} path={["seriesShortCode"]}>
                                {(field) => (
                                    <Field data-invalid={field.errors !== null}>
                                        <FieldLabel htmlFor="create-series-short-code">Series Short Code</FieldLabel>
                                        <Input
                                            {...field.props}
                                            disabled={createAction.isExecuting}
                                            id="create-series-short-code"
                                            value={field.input ?? ""}
                                        />
                                        {field.errors && (
                                            <FieldError errors={field.errors.map((message) => ({ message }))} />
                                        )}
                                    </Field>
                                )}
                            </FormischField>

                            <FormischField of={form} path={["isWorldQualifier"]}>
                                {(field) => (
                                    <Field orientation="horizontal">
                                        <Checkbox
                                            checked={field.input ?? false}
                                            disabled={createAction.isExecuting}
                                            id="create-is-world-qualifier"
                                            onCheckedChange={(checked) => {
                                                setInput(form, {
                                                    input: checked === true,
                                                    path: ["isWorldQualifier"],
                                                });
                                            }}
                                        />
                                        <FieldLabel htmlFor="create-is-world-qualifier">World Qualifier</FieldLabel>
                                    </Field>
                                )}
                            </FormischField>

                            <Field>
                                <FieldLabel>Region</FieldLabel>
                                <EntitySearch
                                    mode="region"
                                    onChange={(item) => {
                                        setSelectedRegion(item);
                                        setInput(form, { input: item?.id ?? "", path: ["regionId"] });
                                    }}
                                    placeholder="Search regions..."
                                    value={selectedRegion}
                                />
                            </Field>
                        </FieldGroup>

                        <DialogFooter className="mt-6">
                            <DialogClose
                                className={buttonVariants({ variant: "outline" })}
                                disabled={createAction.isExecuting}
                                type="button"
                            >
                                Cancel
                            </DialogClose>
                            <Button disabled={createAction.isExecuting || !selectedRegion} type="submit">
                                {createAction.isExecuting ? <Spinner /> : <IconPlus />}
                                Create Event
                            </Button>
                        </DialogFooter>
                    </Form>
                </DialogContent>
            </Dialog>
        </>
    );
}
